import React, { useState } from 'react';
import { Upload, Modal, Progress, List, Button, Space, Tag, message } from 'antd';
import { UploadOutlined, DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { fileManagementApi } from '../lib/fileManagementApi';
import { formatBytes, getFileIcon } from '../utils/fileUtils';

const { Dragger } = Upload;

interface FileUploadProps {
  visible: boolean;
  onClose: () => void;
  bucket: string;
  currentProject?: {
    id: string;
    name: string;
  };
  folderPrefix?: string;
  onUploadComplete: () => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  visible,
  onClose,
  bucket,
  currentProject,
  folderPrefix,
  onUploadComplete,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    if (!bucket) {
      message.error('Please select a bucket first');
      return;
    }

    const uploadingFile: UploadingFile = {
      file,
      progress: 0,
      status: 'uploading',
    };

    setUploadingFiles(prev => [...prev, uploadingFile]);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add folder prefix if provided
      if (folderPrefix) {
        formData.append('prefix', folderPrefix);
      }
      
      if (currentProject) {
        formData.append('metadata', JSON.stringify({
          projectId: currentProject.id,
          projectName: currentProject.name,
          uploadedBy: 'user',
          uploadedAt: new Date().toISOString(),
        }));
      }

      // Simulate progress for demo purposes
      // In real implementation, you'd use the onUploadProgress callback
      const progressInterval = setInterval(() => {
        setUploadingFiles(prev => prev.map(f => 
          f.file === file && f.status === 'uploading'
            ? { ...f, progress: Math.min(f.progress + 10, 90) }
            : f
        ));
      }, 200);

      await fileManagementApi.uploadFile(bucket, formData);

      clearInterval(progressInterval);

      setUploadingFiles(prev => prev.map(f => 
        f.file === file
          ? { ...f, progress: 100, status: 'success' }
          : f
      ));

      message.success(`${file.name} uploaded successfully`);
    } catch (error) {
      setUploadingFiles(prev => prev.map(f => 
        f.file === file
          ? { ...f, status: 'error', error: 'Upload failed' }
          : f
      ));
      message.error(`Failed to upload ${file.name}`);
      console.error('Upload error:', error);
    }
  };

  const handleRemoveFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(f => f.file !== file));
  };

  const handleClose = () => {
    if (uploading) {
      Modal.confirm({
        title: 'Upload in Progress',
        content: 'Files are still being uploaded. Are you sure you want to close?',
        onOk: () => {
          setUploadingFiles([]);
          setUploading(false);
          onClose();
        },
      });
    } else {
      setUploadingFiles([]);
      onClose();
      onUploadComplete();
    }
  };

  const handleDone = () => {
    setUploadingFiles([]);
    setUploading(false);
    onClose();
    onUploadComplete();
  };

  const hasUploading = uploadingFiles.some(f => f.status === 'uploading');
  const hasCompleted = uploadingFiles.some(f => f.status === 'success' || f.status === 'error');

  return (
    <Modal
      title="Upload Files"
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="close" onClick={handleClose} disabled={hasUploading}>
          {hasCompleted ? 'Done' : 'Cancel'}
        </Button>,
        ...(hasCompleted && !hasUploading ? [
          <Button key="done" type="primary" onClick={handleDone}>
            Done
          </Button>
        ] : [])
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Dragger
            multiple
            beforeUpload={(file) => {
              handleUpload(file);
              return false; // Prevent default upload
            }}
            disabled={hasUploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag files to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for single or bulk upload. Files will be uploaded to: <strong>{bucket}</strong>
              {folderPrefix && (
                <><br />Files will be stored in folder: <strong>{folderPrefix}</strong></>
              )}
              {currentProject && (
                <><br />Files will be associated with project: <strong>{currentProject.name}</strong></>
              )}
            </p>
          </Dragger>
        </div>

        {uploadingFiles.length > 0 && (
          <div>
            <h4>Upload Progress</h4>
            <List
              dataSource={uploadingFiles}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    item.status === 'uploading' ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(item.file)}
                      />
                    ),
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={getFileIcon(item.file.type)}
                    title={
                      <Space>
                        {item.file.name}
                        {item.status === 'success' && (
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        )}
                        {item.status === 'error' && (
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        )}
                        {currentProject && (
                          <Tag color="blue">{currentProject.name}</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <span>{formatBytes(item.file.size)}</span>
                        {item.status === 'uploading' && (
                          <Progress percent={item.progress} size="small" />
                        )}
                        {item.status === 'error' && (
                          <span style={{ color: '#ff4d4f' }}>{item.error}</span>
                        )}
                        {item.status === 'success' && (
                          <span style={{ color: '#52c41a' }}>Upload complete</span>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default FileUpload;